import React, { useState } from 'react';
import { Box, Container, Typography, Tabs, Tab, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LaptopMacRoundedIcon from '@mui/icons-material/LaptopMacRounded';
import CloudCircleRoundedIcon from '@mui/icons-material/CloudCircleRounded';

const TAB_ICONS = [
  <ShieldRoundedIcon />,
  <LockRoundedIcon />,
  <BadgeRoundedIcon />,
  <HistoryRoundedIcon />,
];

const ACCENT = '#0F172A';

const SecuritySection: React.FC = () => {
  const { t, r } = useT();
  const [tab, setTab] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const tabsData = [
    r.security.tabs.isolated,
    r.security.tabs.readonly,
    r.security.tabs.identity,
    r.security.tabs.audit,
  ];

  const sandbox = r.security.sandbox;
  const flow = r.security.flow;
  const transports = r.security.transports;

  const activeTab = tabsData[tab];
  const activeAccent = ACCENT;

  return (
    <Box component="section" id="security" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.8rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
            }}
          >
            {t('security.title')}
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 5, color: '#64748B', maxWidth: '850px', mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}
          >
            {t('security.subtitle')}
          </Typography>
        </motion.div>

        <Box
          sx={{
            background: 'white',
            borderRadius: 3,
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#FAFBFC' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: { xs: 1, md: 3 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  minHeight: 64,
                  color: '#64748B',
                  px: { xs: 1.5, md: 3 },
                },
                '& .Mui-selected': { color: `${activeAccent} !important` },
                '& .MuiTabs-indicator': {
                  height: 2,
                  backgroundColor: activeAccent,
                },
              }}
            >
              {tabsData.map((tabData, idx) => (
                <Tab
                  key={tabData.label}
                  icon={TAB_ICONS[idx]}
                  iconPosition="start"
                  label={tabData.label}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#1E293B',
                  mb: 1.5,
                  fontSize: { xs: '1.4rem', md: '1.8rem' },
                }}
              >
                {activeTab.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#475569',
                  lineHeight: 1.7,
                  fontSize: '1rem',
                  mb: 4,
                  maxWidth: '850px',
                }}
              >
                {activeTab.subtitle}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2,
                  mb: tab === 1 || tab === 2 ? 4 : 0,
                }}
              >
                {activeTab.bullets.map((bullet) => (
                  <Box
                    key={bullet.title}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      transition: 'all 0.3s',
                      '&:hover': {
                        background: 'white',
                        border: `1px solid ${activeAccent}55`,
                        transform: 'translateY(-3px)',
                        boxShadow: `0 8px 24px ${activeAccent}1A`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <CheckRoundedIcon sx={{ color: activeAccent, fontSize: '1.3rem', mt: 0.1, flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1rem', lineHeight: 1.3 }}>
                        {bullet.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.88rem', lineHeight: 1.55, pl: 3.5 }}>
                      {bullet.description}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {tab === 1 && (
                <Box
                  sx={{
                    mt: 4,
                    p: { xs: 3, md: 4 },
                    borderRadius: 2,
                    backgroundColor: 'rgb(13, 13, 37)',
                    color: 'white',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.55)',
                      fontSize: '0.75rem',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      mb: 2.5,
                      display: 'block',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    Cada salto, menos privilegios
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    {flow.map((node, idx) => (
                      <React.Fragment key={node}>
                        <Box
                          sx={{
                            px: 2,
                            py: 1.2,
                            borderRadius: 1.5,
                            backgroundColor: `rgba(255, 255, 255, ${Math.max(0.04, 0.16 - idx * 0.03)})`,
                            border: '1px solid rgba(255, 255, 255, 0.18)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            whiteSpace: 'nowrap',
                            opacity: 1 - idx * 0.08,
                          }}
                        >
                          {node}
                        </Box>
                        {idx < flow.length - 1 && (
                          <Box sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: '1.1rem' }}>→</Box>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {transports.map((tr) => (
                      <Chip
                        key={tr}
                        label={tr}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          color: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(255, 255, 255, 0.18)',
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {tab === 2 && (
                <Box
                  sx={{
                    mt: 4,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      backgroundColor: '#F1F5F9',
                      borderBottom: '1px solid #E2E8F0',
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography
                        sx={{
                          color: '#64748B',
                          fontSize: '0.78rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                        }}
                      >
                        {sandbox.title}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderLeft: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LaptopMacRoundedIcon sx={{ color: '#94A3B8', fontSize: '1.2rem' }} />
                      <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '0.9rem' }}>
                        {sandbox.laptop}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderLeft: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudCircleRoundedIcon sx={{ color: '#0F172A', fontSize: '1.2rem' }} />
                      <Typography sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.9rem' }}>
                        {sandbox.wai}
                      </Typography>
                    </Box>
                  </Box>
                  {sandbox.rows.map((row, idx) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC',
                        borderTop: idx === 0 ? 'none' : '1px solid #E2E8F0',
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ color: '#1E293B', fontWeight: 600, fontSize: '0.9rem' }}>
                          {row.label}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, borderLeft: '1px solid #E2E8F0' }}>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.88rem', textDecoration: 'line-through' }}>
                          {row.laptop}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, borderLeft: '1px solid #E2E8F0' }}>
                        <Typography sx={{ color: '#1E293B', fontSize: '0.88rem', fontWeight: 500 }}>
                          {row.wai}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {tab === 3 && (
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: '1px solid #E2E8F0',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#94A3B8',
                      fontSize: '0.88rem',
                      letterSpacing: '0.05em',
                      fontStyle: 'italic',
                    }}
                  >
                    {t('security.frameworksLine')}
                  </Typography>
                </Box>
              )}
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SecuritySection;
